import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  Save, Plus, Trash2, GripVertical, Type, Hash, ChevronDown,
  Calendar, DollarSign, CheckSquare, Image as ImageIcon, ListChecks, AlignLeft, Upload, X, Lock, Check, ArrowLeft, Sparkles, Loader2, History, RotateCcw,
} from "lucide-react";
import { apiClient } from "@/lib/api";
import { resolveBackHref } from "@/lib/navTracker";
import { useCollections } from "@/context/CollectionContext";
import { classifyPresetField } from "@/lib/presetSchemas";
import { CategoryThumb } from "@/components/CategoryPill";
import BottomNav from "@/components/BottomNav";
import EmptyState from "@/components/EmptyState";
import { GRAPE_VARIETIES } from "@/lib/wineFields";
import { getCleanupHistoryCap } from "@/lib/cleanupHistory";
import { useTier } from "@/context/AuthContext";

const FIELD_TYPES = [
  { value: "text",         label: "Text",         Icon: Type },
  { value: "textarea",     label: "Long Text",    Icon: AlignLeft },
  { value: "number",       label: "Number",       Icon: Hash },
  { value: "currency",     label: "Currency",     Icon: DollarSign },
  { value: "date",         label: "Date",         Icon: Calendar },
  { value: "dropdown",     label: "Dropdown",     Icon: ChevronDown },
  { value: "multi_select", label: "Multi-Select", Icon: ListChecks },
  { value: "checkbox",     label: "Checkbox",     Icon: CheckSquare },
  { value: "image",        label: "Image",        Icon: ImageIcon },
];

/**
 * CategoryBuilder — create or edit a category + its field schema.
 * URL: /categories/new           → create
 *      /categories/:id/edit      → edit
 *      /categories/:id/edit?next=/items/new&collection=...  → after save, navigate there
 */
export default function CategoryBuilder() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = !!id;
  const { collections } = useCollections();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState("");
  const [imageTypeError, setImageTypeError] = useState("");
  const [collectionId, setCollectionId] = useState(searchParams.get("collection_id") || "");
  const [fields, setFields] = useState([]);
  const [savingMeta, setSavingMeta] = useState(false);
  const [catId, setCatId] = useState(id || null);
  const [loading, setLoading] = useState(isEdit);
  const imageRef = useRef(null);
  const { isAdmin } = useTier();

  // Derived Create-mode validation state. Explicitly re-computed on
  // every render so React always reflects the latest name /
  // collectionId values. Save is enabled the moment both are
  // non-empty — custom fields are optional (they are flushed inside
  // `saveMeta` if any are queued).
  const createSaveDisabled = savingMeta || !name.trim() || !String(collectionId || "").trim();

  // Load existing category + fields
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const [cats, fs] = await Promise.all([
          apiClient.get("/categories"),
          apiClient.get(`/categories/${id}/fields`),
        ]);
        const cat = (cats.data || []).find((c) => c.id === id);
        if (cat) {
          setName(cat.category_name || "");
          setDescription(cat.category_description || "");
          setImage(cat.image || "");
          setCollectionId(cat.collection_id || "");
        }
        setFields(fs.data || []);
      } catch {
        toast.error("Failed to load category.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit]);

  const onImageFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Strict image-type gate — only JPG / PNG / WEBP. Mirrors the
    // backend allowlist in `_validate_item_image_types`.
    const ALLOWED_MIMES = new Set(["image/jpeg", "image/png", "image/webp"]);
    const ALLOWED_EXTS  = /\.(jpe?g|png|webp)$/i;
    const isAllowed =
      (file.type && ALLOWED_MIMES.has(file.type.toLowerCase())) ||
      (file.name && ALLOWED_EXTS.test(file.name));
    if (!isAllowed) {
      setImageTypeError("Only JPG, PNG, or WEBP images are allowed.");
      return;
    }
    setImageTypeError("");
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Image > 4 MB — please pick a smaller one.");
      return;
    }
    const r = new FileReader();
    r.onload = () => setImage(r.result);
    r.readAsDataURL(file);
  };

  const saveMeta = async () => {
    if (!name.trim()) { toast.error("Category name is required."); return null; }
    setSavingMeta(true);
    try {
      const body = {
        category_name: name.trim(),
        category_description: description.trim(),
        image,
        collection_id: collectionId || null,
      };
      if (catId) {
        await apiClient.put(`/categories/${catId}`, body);
        toast.success("Category saved.");
        return catId;
      } else {
        const { data } = await apiClient.post("/categories", body);
        // Flush any pending custom fields queued on the Create screen.
        // These were built up locally by `addField` / `updateField` /
        // `deleteField` before the category existed. Post them in the
        // order the user arranged, then re-fetch so the fields state
        // reflects real server records (with real ids) instead of the
        // "pending-*" placeholders.
        const pending = fields.filter((f) => f._pending);
        if (pending.length > 0) {
          for (let i = 0; i < pending.length; i++) {
            const f = pending[i];
            try {
              await apiClient.post(`/categories/${data.id}/custom_fields`, {
                field_name: f.field_name,
                field_type: f.field_type,
                required: !!f.required,
                options: Array.isArray(f.options) ? f.options : [],
                order: i,
              });
            } catch (fieldErr) {
              // Non-blocking: report but keep going so the category
              // itself still lands with as many fields as possible.
              toast.error(
                fieldErr?.response?.data?.detail
                || `Failed to save field "${f.field_name}".`
              );
            }
          }
          try {
            const { data: refreshed } = await apiClient.get(`/categories/${data.id}/fields`);
            setFields(refreshed || []);
          } catch {
            // If the refetch fails the pending fields still exist on
            // the server — the UI just won't have live ids until the
            // user reloads. Non-fatal.
          }
        }
        setCatId(data.id);
        toast.success("Category created.");
        return data.id;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save category.");
      return null;
    } finally {
      setSavingMeta(false);
    }
  };

  // === Preset packs — curated, ISOLATED schemas per collection type.
  //     Each preset is a self-contained list. No cross-contamination
  //     between Wine / Whisky / Spirits — fields specific to one
  //     domain never appear in another.
  // CDI-locked schemas — presets no longer mutate master fields (all
  // types are now permanently locked). Selecting a preset simply
  // triggers a re-seed via GET so the master template is re-applied
  // to CDI industry-standard order. Custom fields are untouched.
  const PRESETS = {
    wine: {
      label: "Wine (CDI 20 fields)",
      fields: [
        { field_name: "Producer",         field_type: "text" },
        { field_name: "Item Name",        field_type: "text",         required: true },
        { field_name: "Vintage",          field_type: "number" },
        { field_name: "Country",          field_type: "text" },
        { field_name: "Region",           field_type: "text" },
        { field_name: "Sub-Region",       field_type: "text" },
        { field_name: "Appellation",      field_type: "text" },
        { field_name: "Type",             field_type: "dropdown", options: ["Red", "White", "Rosé", "Sparkling", "Dessert", "Fortified"] },
        { field_name: "Grape(s)",         field_type: "multi_select", options: GRAPE_VARIETIES },
        { field_name: "Bottle Size",      field_type: "text" },
        { field_name: "ABV",              field_type: "number" },
        { field_name: "Classification",   field_type: "text" },
        { field_name: "Drink From",       field_type: "number" },
        { field_name: "Drink To",         field_type: "number" },
        { field_name: "Tasting Notes",    field_type: "textarea" },
        { field_name: "Personal Notes",   field_type: "textarea" },
        { field_name: "Image",            field_type: "image" },
        { field_name: "Storage Location", field_type: "text" },
        { field_name: "Purchase Date",    field_type: "date" },
        { field_name: "Purchase Source",  field_type: "text" },
      ],
    },
    whisky: {
      label: "Whisky (CDI 12 fields)",
      // P1 field-grouping fix: heavy-use identity fields (Distillery → Region
      // → Age → ABV) surface at the top. No fields added, renamed, or
      // removed — order only.
      fields: [
        { field_name: "Distillery",       field_type: "text" },
        { field_name: "Region",           field_type: "text" },
        { field_name: "Age",              field_type: "number" },
        { field_name: "ABV",              field_type: "number" },
        { field_name: "Cask Type",        field_type: "text" },
        { field_name: "Bottling Year",    field_type: "number" },
        { field_name: "Release Type",     field_type: "text" },
        { field_name: "Notes",            field_type: "textarea" },
        { field_name: "Image",            field_type: "image" },
        { field_name: "Storage Location", field_type: "text" },
        { field_name: "Purchase Date",    field_type: "date" },
        { field_name: "Purchase Source",  field_type: "text" },
      ],
    },
    spirits: {
      label: "Spirits (CDI 14 fields)",
      fields: [
        { field_name: "Producer / Distillery", field_type: "text" },
        { field_name: "Item Name",             field_type: "text",     required: true },
        { field_name: "Type",                  field_type: "dropdown", options: ["Rum", "Gin", "Vodka", "Tequila", "Mezcal", "Cognac", "Brandy", "Liqueur", "Bitters", "Other"] },
        { field_name: "Country",               field_type: "text" },
        { field_name: "Region",                field_type: "text" },
        { field_name: "ABV",                   field_type: "number" },
        { field_name: "Age",                   field_type: "number" },
        { field_name: "Cask Type",             field_type: "text" },
        { field_name: "Bottle Size",           field_type: "text" },
        { field_name: "Notes",                 field_type: "textarea" },
        { field_name: "Image",                 field_type: "image" },
        { field_name: "Storage Location",      field_type: "text" },
        { field_name: "Purchase Date",         field_type: "date" },
        { field_name: "Purchase Source",       field_type: "text" },
      ],
    },
  };
  const [presetKey, setPresetKey] = useState("");
  const [applyingPreset, setApplyingPreset] = useState(false);
  // Tracks the most-recently-created custom field so its Field Name
  // input can auto-focus for inline rename immediately after Add Field.
  const [justAddedId, setJustAddedId] = useState(null);

  // Auto-bind the Preset selector to the parent collection's type_id so
  // Wine / Whisky / Spirits categories surface their real preset name
  // (instead of falling back to "Custom (no preset)"). Fires whenever
  // the parent collection resolves and never overrides a manual pick.
  useEffect(() => {
    const parentColl = collections.find((c) => c.id === collectionId);
    const t = parentColl?.type_id;
    if (t && ["wine", "whisky", "spirits"].includes(t)) {
      setPresetKey((prev) => (prev ? prev : t));
    }
  }, [collectionId, collections]);

  // Deep-link support: `?scroll=preset` (from the Categories.jsx Create
  // form's "Manage Fields" link) auto-scrolls to the preset dropdown
  // once the fields section has mounted. Runs once per page load.
  useEffect(() => {
    if (searchParams.get("scroll") !== "preset") return;
    const t = setTimeout(() => {
      const el = document.getElementById("cb-preset");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.focus?.();
      }
    }, 350);
    return () => clearTimeout(t);
  }, [searchParams]);

  // CDI-locked preset apply — simply re-seeds master fields from the
  // backend template (idempotent GET) so master order snaps back to
  // the CDI industry-standard. Custom fields are left untouched. No
  // destructive delete/re-create is performed since all master fields
  // are protected by LOCKED_TEMPLATE_TYPES.
  const applyPreset = async (key) => {
    if (!key || !PRESETS[key]) return;
    if (!catId) {
      toast.error("Save the category first to apply a preset.");
      setPresetKey("");
      return;
    }
    setApplyingPreset(true);
    try {
      // 2026-02-26 — Wine forces a fresh GET (with a cache-buster query
      // param) so the backend seeder unconditionally re-asserts all 20
      // master fields, including Cuvée. Whisky / Spirits keep the plain
      // idempotent GET flow untouched.
      const url = key === "wine"
        ? `/categories/${catId}/fields?reseed=${Date.now()}`
        : `/categories/${catId}/fields`;
      const { data } = await apiClient.get(url);
      setFields(data);
      const masterCount = (data || []).filter((f) => f.master).length;
      if (key === "wine" && masterCount < 20) {
        toast.error(`Wine reset installed ${masterCount}/20 master fields — please re-run.`);
      } else {
        toast.success(`${PRESETS[key].label} — ${masterCount} master field${masterCount === 1 ? "" : "s"} locked to CDI order.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to apply preset.");
    } finally {
      setApplyingPreset(false);
      // Keep the selector reflecting the applied preset so it no
      // longer collapses back to "Custom (no preset)".
      setPresetKey(key);
    }
  };

  // Add Field is gated — user MUST explicitly save the category first.
  const addField = async () => {
    if (!catId) {
      // UX guardrail: on Create, require both Category Name and
      // Collection before queueing any field. Matches the Save
      // Category validation gate so users can't build a schema for
      // a category shell that can't be persisted.
      if (!name.trim() || !collectionId) {
        toast.error("Please enter a Category Name and choose a Collection before adding fields.");
        return;
      }
      // Create-mode: queue the field locally so the user can build the
      // schema before the category is persisted. The pending queue is
      // flushed to `/custom_fields` inside `saveMeta` right after the
      // category POST succeeds — mirrors Edit → Add Field behaviour
      // except the network call is deferred until Save.
      const tmpId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const order = fields.length;
      const pending = {
        id: tmpId,
        field_name: `Field ${order + 1}`,
        field_type: "text",
        required: false,
        options: [],
        master: false,
        order,
        _pending: true,
      };
      setFields((prev) => [pending, ...prev]);
      setJustAddedId(tmpId);
      return;
    }
    try {
      const order = fields.length;
      // Uses /custom_fields so it works on both open and locked
      // template types (Whisky). Custom fields are always inserted as
      // master=false and render AFTER the CDI master block.
      const { data } = await apiClient.post(`/categories/${catId}/custom_fields`, {
        field_name: `Field ${order + 1}`,
        field_type: "text",
        required: false,
        order,
      });
      setFields((prev) => [data, ...prev]);
      // Flag the freshly-inserted field for auto-focus so the user can
      // rename it inline without hunting for the input.
      setJustAddedId(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add field.");
    }
  };

  const updateField = async (fId, patch) => {
    // Optimistic update (also the source of truth for pending fields on
    // the Create screen — no server round-trip until the category is
    // saved).
    setFields((prev) => prev.map((f) => (f.id === fId ? { ...f, ...patch } : f)));
    if (!catId) return;
    try {
      await apiClient.put(`/categories/${catId}/fields/${fId}`, patch);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update field.");
    }
  };

  const deleteField = async (fId) => {
    setFields((prev) => prev.filter((f) => f.id !== fId));
    // Pending fields (Create-mode) live only in local state — no API
    // call needed. Same for missing catId as a defensive guard.
    if (!catId || String(fId).startsWith("pending-")) return;
    try {
      await apiClient.delete(`/categories/${catId}/fields/${fId}`);
    } catch {
      toast.error("Failed to delete field.");
    }
  };

  // -------- Reset to Preset --------
  // Frontend-only tool that surfaces master rows on this category
  // whose field_name is NOT in the current preset's identity or
  // supporting list — i.e. rows seeded by an older preset revision
  // that no longer belong. Only rows with ZERO stored data across
  // every item on this category are safe to delete; any row still
  // holding user data is preserved. Runs against
  // `/categories/{cat_id}/fields/{field_id}` (existing DELETE
  // endpoint — no new routes, no schema changes).
  const [resetBusy, setResetBusy] = useState(false);
  const [resetCandidates, setResetCandidates] = useState([]);
  const [resetOpen, setResetOpen] = useState(false);

  // Derive the preset key from the parent collection so we can classify
  // legacy fields. Non-preset collection types (general, custom, etc.)
  // never have "legacy" fields — every master field is in-preset.
  const parentCollection = collections.find((c) => c.id === collectionId);
  const presetTypeId = parentCollection?.type_id;
  const hasPreset = ["wine", "whisky", "spirits"].includes(presetTypeId);

  const scanLegacyFields = async () => {
    if (!catId || !hasPreset) return;
    setResetBusy(true);
    try {
      // Only master rows whose name isn't in the current preset can
      // be legacy. User-added (master=false) rows aren't preset drift.
      const legacyCandidates = fields.filter(
        (f) => f.master && classifyPresetField(f, presetTypeId) === "legacy"
      );
      if (legacyCandidates.length === 0) {
        toast("No legacy preset fields to clean up.", { icon: "✨" });
        return;
      }
      // Fetch items on this category and drop candidates whose field
      // name still holds data anywhere.
      const { data: items } = await apiClient.get("/items", { params: { category_id: catId } });
      const safeToDelete = legacyCandidates.filter((f) => {
        const key = f.field_name;
        return !items.some((it) => {
          const v = (it.item_data || {})[key];
          if (v === undefined || v === null) return false;
          if (typeof v === "string") return v.trim().length > 0;
          if (Array.isArray(v)) return v.length > 0;
          return true;
        });
      });
      if (safeToDelete.length === 0) {
        toast("Legacy fields still hold data on one or more items — nothing safe to delete.", { icon: "⚠️" });
        return;
      }
      setResetCandidates(safeToDelete);
      setResetOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to scan for legacy fields.");
    } finally {
      setResetBusy(false);
    }
  };

  const confirmResetToPreset = async () => {
    if (resetCandidates.length === 0) return;
    setResetBusy(true);
    try {
      for (const f of resetCandidates) {
        try {
          await apiClient.delete(`/categories/${catId}/fields/${f.id}`);
        } catch { /* keep going; report at end */ }
      }
      // Refetch to sync
      const { data } = await apiClient.get(`/categories/${catId}/fields`);
      setFields(data || []);
      toast.success(`Removed ${resetCandidates.length} legacy field${resetCandidates.length === 1 ? "" : "s"}.`);
      setResetOpen(false);
      setResetCandidates([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reset to preset.");
    } finally {
      setResetBusy(false);
    }
  };

  // -------- Clean Legacy Duplicates --------
  // Client-side hygiene tool: scans user-added fields whose normalised
  // name is within 2 edits of a locked master field name AND which
  // hold no data across items in this category. Renders a confirmation
  // modal listing the safe-to-delete fields. On confirm, deletes via
  // the existing DELETE /categories/{id}/fields/{fId} endpoint — no
  // new backend routes, no schema changes.
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleanupCandidates, setCleanupCandidates] = useState([]);
  const [cleanupBusy, setCleanupBusy] = useState(false);

  const MEANINGFUL_SUFFIXES = ["notes", "story", "details", "history", "description"];

  const normalizeFieldName = (raw) => {
    let s = String(raw || "").toLowerCase();
    // Strip parenthetical qualifiers first: "Bottle Size (ml)" -> "bottle size"
    s = s.replace(/\([^)]*\)/g, " ");
    // Strip trailing unit tokens: " %", " ml", " kg", currency codes, etc.
    s = s.replace(/[\s\-_]+(ml|cl|l|kg|g|oz|lbs|%|usd|nzd|aud|gbp|eur|jpy|hkd|sgd)\s*$/i, " ");
    // Strip stray symbols
    s = s.replace(/[%°]+/g, " ");
    // Drop everything non-alphanumeric
    s = s.replace(/[^a-z0-9]/g, "");
    return s;
  };

  const levenshtein = (a, b) => {
    const m = a.length, n = b.length;
    if (m === 0) return n;
    if (n === 0) return m;
    const prev = new Array(n + 1);
    const curr = new Array(n + 1);
    for (let j = 0; j <= n; j++) prev[j] = j;
    for (let i = 1; i <= m; i++) {
      curr[0] = i;
      for (let j = 1; j <= n; j++) {
        const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
        curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
      }
      for (let j = 0; j <= n; j++) prev[j] = curr[j];
    }
    return prev[n];
  };

  const hasMeaningfulSuffix = (customName, masterName) => {
    // Reject if the custom name adds a semantic suffix ("Notes", "Story",
    // etc.) that the master name lacks — e.g. "Producer Notes" vs
    // "Producer" is NOT a duplicate.
    const c = String(customName || "").toLowerCase();
    const m = String(masterName || "").toLowerCase();
    return MEANINGFUL_SUFFIXES.some((suf) => c.includes(suf) && !m.includes(suf));
  };

  const isFieldEmptyValue = (v) => {
    if (v === undefined || v === null) return true;
    if (typeof v === "string" && v.trim() === "") return true;
    if (Array.isArray(v) && v.length === 0) return true;
    return false;
  };
  const isFieldEmptyAcrossItems = (fieldName, items) => {
    return (items || []).every((it) => isFieldEmptyValue((it.item_data || {})[fieldName]));
  };

  // Client-side merge strategy — per field type. Never overwrites a
  // non-empty master value except for text/textarea (append) and
  // multi_select (union). Callers must ensure `masterField` is the
  // matching CDI master descriptor.
  const mergeValueIntoMaster = (masterCurrent, dupeValue, masterField) => {
    const type = masterField?.field_type || "text";
    if (isFieldEmptyValue(masterCurrent)) return dupeValue;
    if (type === "text" || type === "textarea") {
      const s = String(masterCurrent);
      const d = String(dupeValue);
      // Avoid duplicating the exact same segment.
      const parts = s.split(/;\s*/).map((p) => p.trim());
      if (parts.includes(d.trim())) return s;
      return `${s}; ${d}`;
    }
    if (type === "dropdown") {
      const opts = Array.isArray(masterField.options) ? masterField.options : [];
      return opts.includes(dupeValue) ? dupeValue : masterCurrent;
    }
    if (type === "multi_select") {
      const cur = Array.isArray(masterCurrent) ? masterCurrent : [masterCurrent];
      const add = Array.isArray(dupeValue) ? dupeValue : [dupeValue];
      const opts = Array.isArray(masterField.options) && masterField.options.length > 0 ? masterField.options : null;
      const additions = add.filter((v) => !opts || opts.includes(v));
      return Array.from(new Set([...cur, ...additions]));
    }
    // number, currency, date, checkbox, image → keep master (never overwrite)
    return masterCurrent;
  };

  const handleCleanup = async () => {
    if (!catId) { toast.error("Save the category first to run cleanup."); return; }
    setCleanupBusy(true);
    try {
      const { data: items } = await apiClient.get(`/items?category_id=${catId}`);
      const customFields = fields.filter((f) => !f.master);
      const masters = fields
        .filter((f) => f.master)
        .map((m) => ({ raw: m.field_name, norm: normalizeFieldName(m.field_name), field: m }));
      const candidates = [];
      for (const f of customFields) {
        const norm = normalizeFieldName(f.field_name);
        if (!norm) continue;
        let best = null;
        for (const m of masters) {
          if (!m.norm) continue;
          if (hasMeaningfulSuffix(f.field_name, m.raw)) continue;
          const d = levenshtein(norm, m.norm);
          if (d <= 2 && (best === null || d < best.distance)) {
            best = { master: m.raw, masterField: m.field, distance: d };
          }
        }
        if (!best) continue;
        // Capture non-empty values so the modal can offer Merge as an option
        // instead of only Delete when the dupe field is actually populated.
        const values = [];
        for (const it of (items || [])) {
          const v = (it.item_data || {})[f.field_name];
          if (!isFieldEmptyValue(v)) values.push({ itemId: it.id, value: v, itemData: it.item_data || {} });
        }
        candidates.push({
          field: f,
          matchedMaster: best.master,
          masterField: best.masterField,
          hasValues: values.length > 0,
          values,
        });
      }
      // Initial per-candidate action defaults: Merge when the field has
      // values, Delete when it is empty.
      const defaults = {};
      candidates.forEach((c) => { defaults[c.field.id] = c.hasValues ? "merge" : "delete"; });
      setCandidateActions(defaults);
      setCleanupCandidates(candidates);
      setCleanupOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to scan for legacy duplicates.");
    } finally {
      setCleanupBusy(false);
    }
  };

  // Per-candidate action selector: "merge" (values only) or "delete".
  const [candidateActions, setCandidateActions] = useState({});

  const confirmCleanup = async () => {
    if (cleanupCandidates.length === 0) { setCleanupOpen(false); return; }
    setCleanupBusy(true);
    let deleteOk = 0, deleteFail = 0;
    let mergeOk = 0, mergeFail = 0;
    const removedSnapshots = [];
    const mergedSnapshots = [];

    for (const c of cleanupCandidates) {
      const action = candidateActions[c.field.id] || (c.hasValues ? "merge" : "delete");
      if (action === "merge" && c.hasValues) {
        // Move every non-empty value into the matching master field, then
        // drop the duplicate field.
        let anyMergeFailed = false;
        const mergedValues = [];
        for (const v of c.values) {
          try {
            const currentData = { ...(v.itemData || {}) };
            const masterCurrent = currentData[c.matchedMaster];
            const nextMaster = mergeValueIntoMaster(masterCurrent, v.value, c.masterField);
            currentData[c.matchedMaster] = nextMaster;
            // Drop the legacy duplicate key so item_data stays clean.
            delete currentData[c.field.field_name];
            await apiClient.patch(`/items/${v.itemId}`, { item_data: currentData });
            mergedValues.push({ itemId: v.itemId, value: v.value });
            mergeOk += 1;
          } catch {
            anyMergeFailed = true;
            mergeFail += 1;
          }
        }
        // Only delete the field if every value was moved successfully to
        // avoid orphaning still-populated rows.
        if (!anyMergeFailed) {
          try {
            await apiClient.delete(`/categories/${catId}/fields/${c.field.id}`);
            deleteOk += 1;
            mergedSnapshots.push({
              fieldId: c.field.id,
              fieldName: c.field.field_name,
              matchedMaster: c.matchedMaster,
              values: mergedValues,
            });
            removedSnapshots.push({
              fieldId: c.field.id,
              fieldName: c.field.field_name,
              fieldType: c.field.field_type,
              required: !!c.field.required,
              options: Array.isArray(c.field.options) ? c.field.options : [],
              matchedMaster: c.matchedMaster,
              action: "merge",
            });
          } catch { deleteFail += 1; }
        }
      } else {
        // Plain delete — either the field was empty or the user chose it.
        try {
          await apiClient.delete(`/categories/${catId}/fields/${c.field.id}`);
          deleteOk += 1;
          removedSnapshots.push({
            fieldId: c.field.id,
            fieldName: c.field.field_name,
            fieldType: c.field.field_type,
            required: !!c.field.required,
            options: Array.isArray(c.field.options) ? c.field.options : [],
            matchedMaster: c.matchedMaster,
            action: "delete",
          });
        } catch { deleteFail += 1; }
      }
    }

    // Reload fields so the UI reflects the on-server state.
    if (deleteOk > 0) {
      setFields((prev) => prev.filter((f) => !removedSnapshots.some((s) => s.fieldId === f.id)));
    }

    // Append a run record to localStorage so the user can audit / undo later.
    if (removedSnapshots.length > 0 || mergedSnapshots.length > 0) {
      const rec = {
        timestamp: new Date().toISOString(),
        removedFields: removedSnapshots,
        mergedFields: mergedSnapshots,
      };
      const prev = readCleanupHistory(catId);
      const next = [...prev, rec].slice(-getCleanupHistoryCap());
      writeCleanupHistory(catId, next);
      setCleanupHistory(next);
    }

    setCleanupOpen(false);
    setCleanupCandidates([]);
    setCandidateActions({});
    setCleanupBusy(false);

    // Toast copy varies by which actions actually ran.
    if (deleteFail === 0 && mergeFail === 0) {
      if (mergedSnapshots.length > 0 && removedSnapshots.some((s) => s.action === "delete")) {
        toast.success("Merged into master. Legacy fields cleaned.");
      } else if (mergedSnapshots.length > 0) {
        toast.success("Merged into master.");
      } else {
        toast.success("Legacy fields cleaned.");
      }
    } else {
      toast.error(
        `Cleaned ${deleteOk}, merged ${mergeOk}, failed ${deleteFail + mergeFail}.`
      );
    }
  };

  // ---- Cleanup History (localStorage, per-category) ----
  // Retention cap: only the last N runs are ever persisted per category.
  // Older entries are discarded on the next write. Prevents unbounded
  // growth and keeps well under the ~5 MB browser quota.
  // Cap is user-configurable from Settings → Cleanup History; the helper
  // reads localStorage fresh each call so a mid-session change from the
  // Settings page trims the next write without needing a reload.
  const CLEANUP_HISTORY_KEY = (id) => `cdi:cleanup_history:${id}`;
  const readCleanupHistory = (id) => {
    if (!id || typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem(CLEANUP_HISTORY_KEY(id));
      const arr = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(arr)) return [];
      // Defensive cap on read too — any pre-cap payload gets tidied here.
      return arr.slice(-getCleanupHistoryCap());
    } catch { return []; }
  };
  const writeCleanupHistory = (id, list) => {
    if (!id || typeof window === "undefined") return;
    try {
      const capped = Array.isArray(list) ? list.slice(-getCleanupHistoryCap()) : [];
      window.localStorage.setItem(CLEANUP_HISTORY_KEY(id), JSON.stringify(capped));
    }
    catch { /* quota or private-browsing — history is best-effort */ }
  };

  const [historyOpen, setHistoryOpen] = useState(false);
  const [cleanupHistory, setCleanupHistory] = useState([]);
  const [undoBusy, setUndoBusy] = useState(false);

  const openHistory = () => {
    setCleanupHistory(readCleanupHistory(catId));
    setHistoryOpen(true);
  };

  const undoLastCleanup = async () => {
    const history = readCleanupHistory(catId);
    if (history.length === 0) { toast.error("Nothing to undo."); return; }
    const last = history[history.length - 1];
    if (!last?.removedFields?.length) { toast.error("Last run had no fields."); return; }

    setUndoBusy(true);
    let ok = 0, fail = 0;
    const restored = [];
    for (const rf of last.removedFields) {
      try {
        // Recreate via /custom_fields — master schema is protected server-side,
        // so this can only ever bring back a non-master field.
        const { data } = await apiClient.post(`/categories/${catId}/custom_fields`, {
          field_name: rf.fieldName,
          field_type: rf.fieldType || "text",
          required: !!rf.required,
          options: Array.isArray(rf.options) ? rf.options : [],
        });
        restored.push(data);
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    if (restored.length > 0) {
      setFields((prev) => [...prev, ...restored]);
    }
    // Pop the last run from history regardless — a partial restore is still
    // considered "handled" so we don't leave a stale record on top.
    const nextHistory = history.slice(0, -1);
    writeCleanupHistory(catId, nextHistory);
    setCleanupHistory(nextHistory);
    setHistoryOpen(false);
    setUndoBusy(false);
    if (fail === 0) toast.success("Cleanup undone.");
    else toast.error(`Restored ${ok}, failed ${fail}.`);
  };

  const moveField = async (idx, dir) => {
    const next = [...fields];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setFields(next);
    if (catId) {
      try {
        await apiClient.put(`/categories/${catId}/fields/reorder`, { order: next.map((f) => f.id) });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Reorder failed.");
      }
    }
  };

  const handleSaveAndNext = async () => {
    const cid = catId || (await saveMeta());
    if (!cid) return;
    if (catId) await saveMeta(); // ensure metadata is persisted
    const next = searchParams.get("next");
    if (next) {
      // Honored when the caller explicitly set `?next=...` (e.g. an
      // in-flight item-creation flow that detoured here). Resume that flow.
      navigate(`${next}${next.includes("?") ? "&" : "?"}category_id=${cid}`);
    } else {
      // Per the unified-Save spec, Save always returns the user to the
      // Item creation screen. The category context is preserved server-side
      // and the saved category is visible there.
      navigate(`/items/new?category_id=${cid}`);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="vp-card p-8 vp-skeleton h-64" />
      </div>
    );
  }

  // Back — explicit parent route: always /collections/{collectionId}.
  // No history/tracker consulted. Falls back to /categories only when the
  // category is orphan (no collection_id).
  const backHref = collectionId ? `/collections/${collectionId}` : "/categories";

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-10" data-testid="category-builder">
      <button
        type="button"
        onClick={() => navigate(backHref, { replace: true })}
        data-testid="cb-back"
        aria-label="Back to previous screen"
        className="vp-btn vp-btn-secondary vp-btn-sm mb-3 inline-flex items-center gap-2"
      >
        <ArrowLeft size={14} />
        <span>Back</span>
      </button>
      {/* Breadcrumb: Home → Categories / [Category] */}
      <nav
        aria-label="Breadcrumb"
        data-testid="cb-breadcrumb"
        className="flex items-center gap-1.5 flex-wrap text-xs mb-4"
      >
        <Link
          to="/"
          data-testid="cb-breadcrumb-home"
          className="text-[10px] uppercase tracking-wider font-medium vp-muted px-2 py-0.5 rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--gold-strong))] dark:hover:text-[hsl(var(--gold))] transition-colors duration-fast"
        >
          Home
        </Link>
        <span className="vp-muted">→</span>
        <Link
          to="/categories"
          data-testid="cb-breadcrumb-categories"
          className="text-[10px] uppercase tracking-wider font-medium vp-muted px-2 py-0.5 rounded-full border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--gold-strong))] dark:hover:text-[hsl(var(--gold))] transition-colors duration-fast"
        >
          Categories
        </Link>
        <span className="vp-muted">/</span>
        <span
          data-testid="cb-breadcrumb-current"
          className="text-xs font-medium truncate max-w-[40ch]"
          title={isEdit ? (name || "Untitled") : "New Category"}
        >
          {isEdit ? (name || "Untitled") : "New Category"}
        </span>
      </nav>

      {isEdit ? (
        <>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <h1
                className="text-3xl sm:text-4xl font-bold tracking-tight"
                data-testid="cb-title"
              >
                {name || "Untitled"}
              </h1>
              <p className="text-sm vp-muted mt-2" data-testid="cb-subtitle">Category overview</p>
              {description ? (
                <p className="text-sm mt-2" data-testid="cb-description">{description}</p>
              ) : null}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={handleCleanup}
                disabled={cleanupBusy || savingMeta}
                data-testid="cb-clean-legacy"
                title="Scan and remove empty user-added fields whose names duplicate CDI master fields (e.g. 'ABV %' vs 'ABV')."
                className="vp-btn vp-btn-secondary vp-btn-sm disabled:opacity-50 inline-flex"
              >
                {cleanupBusy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                <span className="hidden sm:inline">Clean Legacy Duplicates</span>
                <span className="sm:hidden">Clean</span>
              </button>
              <button
                onClick={openHistory}
                data-testid="cb-cleanup-history"
                title="View past Clean Legacy Duplicates runs and optionally undo the most recent one."
                className="vp-btn vp-btn-ghost vp-btn-sm border border-[hsl(var(--border))] inline-flex"
              >
                <History size={13} />
                <span className="hidden sm:inline">Cleanup History</span>
                <span className="sm:hidden">History</span>
              </button>
              <button
                onClick={handleSaveAndNext}
                disabled={savingMeta}
                data-testid="cb-save-top"
                title="Save category details and schema, then return to Item creation"
                className="vp-btn vp-btn-primary disabled:opacity-50"
              >
                <Save size={15} /> {savingMeta ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
          <div className="mb-6" />
        </>
      ) : (
        <>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <h1
                className="text-3xl sm:text-4xl font-bold tracking-tight"
                data-testid="cb-title"
              >
                Create a new category
              </h1>
              <p className="text-sm vp-muted mt-2 mb-6" data-testid="cb-subtitle">
                Define the category metadata and dynamic fields below.
              </p>
            </div>
            <button
              onClick={handleSaveAndNext}
              disabled={createSaveDisabled}
              data-testid="cb-save-top"
              title={
                !name.trim() ? "Enter a category name to enable Save"
                : !collectionId ? "Pick a collection to enable Save"
                : "Save category details and schema, then return to Item creation"
              }
              className="vp-btn vp-btn-primary disabled:opacity-50 shrink-0"
            >
              <Save size={15} /> {savingMeta ? "Saving…" : "Save Category"}
            </button>
          </div>
        </>
      )}

      <section className="vp-card p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Category Details</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium mb-1.5 vp-muted">Name <span className="text-[hsl(var(--loss))]">*</span></label>
            <input
              data-testid="cb-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Watches, Stamps, Vinyl Records"
              className="vp-input"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5 vp-muted">Description</label>
            <input
              data-testid="cb-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What goes in this category?"
              className="vp-input"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5 vp-muted">Collection</label>
            <select
              data-testid="cb-collection"
              value={collectionId}
              onChange={(e) => setCollectionId(e.target.value)}
              className="vp-select w-full"
            >
              <option value="">— None —</option>
              {collections.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <p className="text-[11px] vp-muted mt-2">Items created with this category will automatically belong to this collection.</p>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5 vp-muted">Category Photo (optional)</label>
            <div className="flex items-center gap-3 flex-wrap">
              <CategoryThumb image={image} size="lg" testid="cb-image-preview" />
              <button
                type="button"
                onClick={() => imageRef.current?.click()}
                data-testid="cb-upload-image"
                className="vp-btn vp-btn-secondary vp-btn-sm"
              >
                <Upload size={14} /> {image ? "Replace Photo" : "Upload Photo"}
              </button>
              {image && (
                <button
                  type="button"
                  onClick={() => setImage("")}
                  data-testid="cb-remove-image"
                  className="vp-btn vp-btn-ghost vp-btn-sm text-[hsl(var(--loss))] border border-[hsl(var(--loss))]"
                >
                  <X size={13} /> Remove Photo
                </button>
              )}
              <input
                ref={imageRef}
                type="file"
                accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                onChange={onImageFile}
                className="hidden"
                data-testid="cb-image-input"
              />
            </div>
            {imageTypeError && (
              <p data-testid="cb-image-type-error" className="text-[11px] text-[hsl(var(--loss))] mt-2">
                {imageTypeError}
              </p>
            )}
            <p className="text-[11px] vp-muted mt-2">Used as this category{"'"}s thumbnail throughout the app. If empty, a neutral placeholder is shown.</p>
          </div>
        </div>
      </section>

      <section className="vp-card p-6 mb-6" data-testid="cb-fields-section">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h2 className="text-lg font-semibold">Manage Fields</h2>
            <p className="text-xs vp-muted">This screen lets you apply presets or add custom fields to your category.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label htmlFor="cb-preset" className="text-xs vp-muted">Preset</label>
            <select
              id="cb-preset"
              value={presetKey}
              onChange={(e) => { const v = e.target.value; setPresetKey(v); if (v) applyPreset(v); }}
              disabled={!catId || hasPreset}
              data-testid="cb-preset-select"
              title="Applying a preset will add locked CDI master fields. Custom fields remain editable and appear above the preset block."
              className="vp-select vp-select-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {Object.entries(PRESETS).map(([k, p]) => (
                <option key={k} value={k}>{p.label}</option>
              ))}
            </select>
            <button
              onClick={addField}
              data-testid="cb-add-field"
              disabled={applyingPreset}
              title="Add a custom field to this category"
              className="vp-btn vp-btn-primary vp-btn-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Plus size={14} /> Add Field
            </button>
            {catId && hasPreset && (
              <button
                onClick={scanLegacyFields}
                data-testid="cb-reset-to-preset"
                disabled={resetBusy || applyingPreset}
                title="Remove legacy preset fields that no longer belong to the current CDI preset and hold no data. Fields with stored data are always preserved."
                className="vp-btn vp-btn-ghost vp-btn-sm border border-[hsl(var(--border))] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {resetBusy ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                Reset to Preset
              </button>
            )}
          </div>
        </div>

        {/* Manage Fields guidance — verbatim CDI copy per Unified
            Category Field UI patch B. Single panel that lays out the
            four rules users need to know at a glance. */}
        <div
          data-testid="cb-manage-fields-guidance"
          className="mb-5 rounded-lg border border-[hsl(var(--gold))]/40 bg-[hsla(35,47%,60%,0.06)] px-4 py-3"
        >
          <div className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--gold))] mb-2 flex items-center gap-2">
            <Lock size={12} /> Manage Fields — how it works
          </div>
          <ul className="text-xs vp-muted leading-relaxed list-disc pl-5 space-y-1">
            <li>Locked fields are part of the CDI preset and cannot be removed.</li>
            <li>Tick optional fields to show them on the item form.</li>
            <li>Untick optional fields to hide them.</li>
            <li>Add custom fields using <span className="font-medium text-[hsl(var(--text))]">Add Field</span>.</li>
          </ul>
        </div>

        {!catId && fields.length === 0 ? (
          <div className="text-center py-10 vp-muted text-sm flex flex-col items-center gap-2" data-testid="cb-fields-locked">
            <Lock size={18} className="vp-subtle" />
            No fields yet. Click <span className="font-medium text-[hsl(var(--text))]">Add Field</span> to queue one, or fill Category Details and click <span className="font-medium text-[hsl(var(--text))]">Save</span> to persist.
          </div>
        ) : fields.length === 0 ? (
          <div className="text-center py-10 vp-muted text-sm">
            No fields yet. Click <span className="font-medium">Add Field</span> to define your first one.
          </div>
        ) : (
          (() => {
            // Split by master flag so the CDI locked block and user
            // custom block render in their own visually-tagged groups.
            // Order within each group is preserved from the API response
            // (which is already sorted by the seeder's template index).
            const masterFields = fields.filter((f) => f.master);
            const customFields = fields.filter((f) => !f.master);
            const masterBase = 0;
            const customBase = masterFields.length;
            return (
              <div className="space-y-6">
                <div data-testid="cb-custom-section">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--gold))]">
                      Custom Fields
                    </h3>
                    {customFields.length > 0 && (
                      <span className="text-[10px] font-semibold uppercase tracking-wider vp-muted">
                        {customFields.length} field{customFields.length === 1 ? "" : "s"}
                      </span>
                    )}
                  </div>
                  {customFields.length === 0 ? (
                    <EmptyState
                      Icon={AlignLeft}
                      title="No custom fields"
                      body="Add fields to capture more details about your items."
                      actionLabel="Add field"
                      actionOnClick={addField}
                      testid="cb-custom-empty"
                    />
                  ) : (
                    <ul className="space-y-3">
                      {customFields.map((f, i) => (
                        <FieldRow
                          key={f.id}
                          f={f}
                          idx={customBase + i}
                          total={fields.length}
                          justAdded={f.id === justAddedId}
                          onFocused={() => setJustAddedId(null)}
                          onUpdate={(patch) => updateField(f.id, patch)}
                          onDelete={() => deleteField(f.id)}
                          onMove={(dir) => moveField(customBase + i, dir)}
                        />
                      ))}
                    </ul>
                  )}
                </div>
                {masterFields.length > 0 && (
                  <div data-testid="cb-master-section">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--gold))]">
                        CDI Master Fields
                      </h3>
                      <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-[hsl(var(--gold))]">
                        <Lock size={11} />
                        Locked · {masterFields.length}
                      </span>
                    </div>
                    <ul className="space-y-3">
                      {masterFields.map((f, i) => (
                        <FieldRow
                          key={f.id}
                          f={f}
                          idx={masterBase + i}
                          total={fields.length}
                          onUpdate={(patch) => updateField(f.id, patch)}
                          onDelete={() => deleteField(f.id)}
                          onMove={(dir) => moveField(masterBase + i, dir)}
                        />
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })()
        )}
      </section>

      {isEdit && isAdmin && catId ? (
        <RareValuesScan catId={catId} />
      ) : null}

      <div className="flex flex-wrap justify-end gap-2">
        <Link to="/categories" className="vp-btn vp-btn-ghost vp-btn-sm border border-[hsl(var(--border))]">Cancel</Link>
      </div>

      <BottomNav current="categories" />

      {/* Reset to Preset confirmation modal — drops master rows that
          are no longer in the current CDI preset AND hold no data
          across any item on this category. */}
      {resetOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/55 backdrop-blur-sm vp-fade-in"
          data-testid="cb-reset-modal"
          onMouseDown={(e) => { if (e.target === e.currentTarget && !resetBusy) setResetOpen(false); }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="cb-reset-title"
            className="w-full sm:max-w-md bg-[hsl(var(--bg-elevated))] border border-[hsl(var(--border))] rounded-t-2xl sm:rounded-lg shadow-xl flex flex-col max-h-[90vh]"
          >
            <div className="px-5 pt-5 pb-3 border-b border-[hsl(var(--border))]">
              <h3 id="cb-reset-title" className="text-base font-semibold flex items-center gap-2">
                <RotateCcw size={16} className="text-[hsl(var(--gold))]" />
                Reset to Preset
              </h3>
              <p className="text-xs vp-muted mt-1">
                The following legacy master fields are no longer part of the CDI preset and hold no data.
                Deleting them removes them permanently from this category.
              </p>
            </div>
            <div className="px-5 py-3 overflow-y-auto flex-1">
              <ul className="text-sm space-y-1.5" data-testid="cb-reset-list">
                {resetCandidates.map((f) => (
                  <li key={f.id} className="flex items-center gap-2 vp-muted">
                    <Lock size={11} className="text-[hsl(var(--gold))]" />
                    <span className="font-medium text-[hsl(var(--text))]">{f.field_name}</span>
                    <span className="text-[10px] uppercase tracking-wider">{f.field_type}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="px-5 py-3 border-t border-[hsl(var(--border))] flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setResetOpen(false)}
                disabled={resetBusy}
                data-testid="cb-reset-cancel"
                className="vp-btn vp-btn-ghost vp-btn-sm border border-[hsl(var(--border))] disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmResetToPreset}
                disabled={resetBusy}
                data-testid="cb-reset-confirm"
                className="vp-btn vp-btn-sm bg-[hsl(var(--loss))] text-white hover:opacity-90 disabled:opacity-40"
              >
                {resetBusy ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                Delete {resetCandidates.length} field{resetCandidates.length === 1 ? "" : "s"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clean Legacy Duplicates confirmation modal */}
      {cleanupOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/55 backdrop-blur-sm vp-fade-in"
          data-testid="cb-cleanup-modal"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !cleanupBusy) setCleanupOpen(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="cb-cleanup-title"
            className="w-full sm:max-w-md bg-[hsl(var(--bg-elevated))] border border-[hsl(var(--border))] rounded-t-2xl sm:rounded-lg shadow-xl flex flex-col max-h-[90vh]"
          >
            <div className="px-5 py-4 border-b border-[hsl(var(--border))] flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles size={14} className="text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]" />
                  <h2 id="cb-cleanup-title" data-testid="cb-cleanup-title" className="text-base font-semibold tracking-tight">
                    Clean Legacy Duplicates
                  </h2>
                </div>
                <p className="text-xs vp-muted">
                  {cleanupCandidates.length === 0
                    ? "No legacy duplicates found. Nothing to clean up."
                    : `We found ${cleanupCandidates.length} legacy field${cleanupCandidates.length === 1 ? "" : "s"} that match master fields and contain no data. Do you want to remove ${cleanupCandidates.length === 1 ? "it" : "them"}?`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setCleanupOpen(false)}
                disabled={cleanupBusy}
                data-testid="cb-cleanup-close"
                aria-label="Close"
                className="vp-btn vp-btn-ghost vp-btn-sm !w-8 !h-8 !p-0 border border-[hsl(var(--border))] text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]"
              >
                <X size={14} />
              </button>
            </div>

            {cleanupCandidates.length > 0 && (
              <div className="px-5 py-3 overflow-y-auto" data-testid="cb-cleanup-list">
                <ul className="text-xs divide-y divide-[hsl(var(--border))]">
                  {cleanupCandidates.map((c) => {
                    const action = candidateActions[c.field.id] || (c.hasValues ? "merge" : "delete");
                    return (
                      <li key={c.field.id} className="py-2.5 space-y-1.5">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-medium truncate">{c.field.field_name}</div>
                            <div className="vp-muted text-[11px]">
                              → matches <span className="italic">{c.matchedMaster}</span>
                              {c.hasValues && (
                                <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full uppercase tracking-wider bg-[hsl(var(--gold))]/15 text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]">
                                  {c.values.length} value{c.values.length === 1 ? "" : "s"}
                                </span>
                              )}
                            </div>
                          </div>
                          {c.hasValues ? (
                            <div className="inline-flex rounded-md border border-[hsl(var(--border))] overflow-hidden shrink-0" role="group" aria-label="Cleanup action">
                              <button
                                type="button"
                                onClick={() => setCandidateActions((m) => ({ ...m, [c.field.id]: "merge" }))}
                                data-testid={`cb-action-merge-${c.field.id}`}
                                aria-pressed={action === "merge"}
                                className={
                                  "px-2.5 py-1 text-[11px] font-medium " +
                                  (action === "merge"
                                    ? "bg-[hsl(var(--gold))]/20 text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]"
                                    : "vp-muted hover:bg-[hsl(var(--accent))]")
                                }
                              >
                                Merge into master
                              </button>
                              <button
                                type="button"
                                onClick={() => setCandidateActions((m) => ({ ...m, [c.field.id]: "delete" }))}
                                data-testid={`cb-action-delete-${c.field.id}`}
                                aria-pressed={action === "delete"}
                                className={
                                  "px-2.5 py-1 text-[11px] font-medium border-l border-[hsl(var(--border))] " +
                                  (action === "delete"
                                    ? "bg-[hsl(var(--loss))]/15 text-[hsl(var(--loss))]"
                                    : "vp-muted hover:bg-[hsl(var(--accent))]")
                                }
                              >
                                Delete
                              </button>
                            </div>
                          ) : (
                            <span className="text-[10px] uppercase tracking-wider vp-muted shrink-0">Empty · will delete</span>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            <div className="px-5 py-3 border-t border-[hsl(var(--border))] flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setCleanupOpen(false)}
                disabled={cleanupBusy}
                data-testid="cb-cleanup-cancel"
                className="vp-btn vp-btn-ghost vp-btn-sm border border-[hsl(var(--border))]"
              >
                Cancel
              </button>
              {cleanupCandidates.length > 0 && (
                <button
                  type="button"
                  onClick={confirmCleanup}
                  disabled={cleanupBusy}
                  data-testid="cb-cleanup-confirm"
                  className="vp-btn vp-btn-danger vp-btn-sm disabled:opacity-50"
                >
                  {cleanupBusy ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                  {cleanupBusy ? "Cleaning…" : "Confirm"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cleanup History modal */}
      {historyOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/55 backdrop-blur-sm vp-fade-in"
          data-testid="cb-history-modal"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !undoBusy) setHistoryOpen(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="cb-history-title"
            className="w-full sm:max-w-lg bg-[hsl(var(--bg-elevated))] border border-[hsl(var(--border))] rounded-t-2xl sm:rounded-lg shadow-xl flex flex-col max-h-[90vh]"
          >
            <div className="px-5 py-4 border-b border-[hsl(var(--border))] flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <History size={14} className="text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]" />
                  <h2 id="cb-history-title" className="text-base font-semibold tracking-tight">
                    Cleanup History
                  </h2>
                </div>
                <p className="text-xs vp-muted">
                  {cleanupHistory.length === 0
                    ? "No cleanup runs recorded for this category yet."
                    : `Past runs are shown newest first. Only the most recent run can be undone — Undo restores deleted field definitions but does not reverse merged values.`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setHistoryOpen(false)}
                disabled={undoBusy}
                data-testid="cb-history-close"
                aria-label="Close"
                className="vp-btn vp-btn-ghost vp-btn-sm !w-8 !h-8 !p-0 border border-[hsl(var(--border))] text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]"
              >
                <X size={14} />
              </button>
            </div>

            {cleanupHistory.length > 0 && (
              <div className="px-5 py-3 overflow-y-auto space-y-3" data-testid="cb-history-list">
                {[...cleanupHistory].reverse().map((run, i) => {
                  const isMostRecent = i === 0;
                  const when = new Date(run.timestamp);
                  const whenStr = isNaN(when.getTime())
                    ? run.timestamp
                    : when.toLocaleString(undefined, {
                        year: "numeric", month: "short", day: "numeric",
                        hour: "2-digit", minute: "2-digit",
                      });
                  return (
                    <div
                      key={run.timestamp + "-" + i}
                      data-testid={`cb-history-run-${i}`}
                      className={`rounded-md border ${isMostRecent ? "border-[hsl(var(--gold))]/40 bg-[hsla(35,47%,60%,0.06)]" : "border-[hsl(var(--border))]"} px-3 py-2`}
                    >
                      <div className="flex items-center justify-between gap-3 mb-1.5">
                        <div className="flex items-center gap-2 text-xs">
                          <span className="font-medium">{whenStr}</span>
                          {isMostRecent && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full uppercase tracking-wider bg-[hsl(var(--gold))]/15 text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]">
                              Most recent
                            </span>
                          )}
                        </div>
                        {isMostRecent && (
                          <button
                            type="button"
                            onClick={undoLastCleanup}
                            disabled={undoBusy}
                            data-testid="cb-history-undo"
                            className="vp-btn vp-btn-secondary vp-btn-sm disabled:opacity-50"
                          >
                            {undoBusy ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
                            {undoBusy ? "Undoing…" : "Undo"}
                          </button>
                        )}
                      </div>
                      <ul className="text-[11px] divide-y divide-[hsl(var(--border))]">
                        {(run.removedFields || []).map((f, j) => (
                          <li key={f.fieldId || j} className="py-1 flex items-center justify-between gap-3">
                            <span className="font-medium truncate">{f.fieldName}</span>
                            <span className="vp-muted shrink-0 flex items-center gap-2">
                              → matched <span className="italic">{f.matchedMaster}</span>
                              <span className={
                                "text-[10px] px-1.5 py-0.5 rounded-full uppercase tracking-wider " +
                                (f.action === "merge"
                                  ? "bg-[hsl(var(--gold))]/15 text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]"
                                  : "bg-[hsl(var(--muted))] vp-muted")
                              }>
                                {f.action === "merge" ? "Merged" : "Deleted"}
                              </span>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="px-5 py-3 border-t border-[hsl(var(--border))] flex justify-end">
              <button
                type="button"
                onClick={() => setHistoryOpen(false)}
                disabled={undoBusy}
                data-testid="cb-history-done"
                className="vp-btn vp-btn-primary vp-btn-sm"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FieldRow({ f, idx, total, onUpdate, onDelete, onMove, justAdded = false, onFocused }) {
  const [localName, setLocalName] = useState(f.field_name);
  const [optionsText, setOptionsText] = useState((f.options || []).join("\n"));
  const [confirmDel, setConfirmDel] = useState(false);
  const nameInputRef = useRef(null);
  // Sync local form state when the parent prop changes (e.g. after server refresh).
  useEffect(() => { setLocalName(f.field_name); }, [f.field_name]);
  useEffect(() => { setOptionsText((f.options || []).join("\n")); }, [f.options]);
  // Auto-focus + select the Field Name input immediately when the row
  // is freshly created (custom field only — master fields are locked
  // so the `readOnly` guard on the input keeps the focus a no-op even
  // if the flag fires by mistake).
  useEffect(() => {
    if (!justAdded) return;
    const el = nameInputRef.current;
    if (!el) return;
    el.focus();
    el.select();
    onFocused?.();
  }, [justAdded, onFocused]);

  const needsOptions = f.field_type === "dropdown" || f.field_type === "multi_select";
  // Master Template fields are locked — Wine / Whisky / Spirits categories
  // all share one canonical field list per collection type. Users can
  // still reorder them and edit dropdown options, but the name / type /
  // required flag / delete action are frozen. This is what guarantees
  // every Wine sub-category shows the same identity fields as every
  // other, on every tenant, forever.
  const isMaster = !!f.master;

  return (
    <li className="border border-[hsl(var(--border))] rounded-lg p-4 bg-[hsl(var(--bg))]" data-testid={`cb-field-${idx}`}>
      <div className="flex items-start gap-3">
        <div className="flex flex-col gap-1 pt-2">
          <button onClick={() => onMove(-1)} disabled={idx === 0} aria-label="Move up" className="vp-muted hover:text-[hsl(var(--text))] disabled:opacity-30 text-xs">▲</button>
          <GripVertical size={12} className="vp-subtle" />
          <button onClick={() => onMove(1)} disabled={idx === total - 1} aria-label="Move down" className="vp-muted hover:text-[hsl(var(--text))] disabled:opacity-30 text-xs">▼</button>
        </div>
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-12 gap-3">
          <div className="sm:col-span-5">
            <label className="block text-[10px] font-medium uppercase tracking-wider mb-1 vp-muted flex items-center gap-2">
              <span>Field Name</span>
              {isMaster && (
                <span
                  data-testid={`cb-field-master-badge-${idx}`}
                  title="This field is part of the CDI preset and cannot be removed."
                  className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--gold))] text-[hsl(var(--gold))] px-1.5 py-[1px] text-[9px] font-semibold tracking-wider uppercase"
                >
                  <Lock size={9} />
                  Locked
                </span>
              )}
            </label>
            <input
              ref={nameInputRef}
              value={localName}
              onChange={(e) => setLocalName(e.target.value)}
              onBlur={() => localName.trim() && localName !== f.field_name && onUpdate({ field_name: localName.trim() })}
              data-testid={`cb-field-name-${idx}`}
              readOnly={isMaster}
              disabled={isMaster}
              className={`vp-input !py-2 !text-sm ${isMaster ? "opacity-60 cursor-not-allowed" : ""}`}
            />
          </div>
          <div className="sm:col-span-4">
            <label className="block text-[10px] font-medium uppercase tracking-wider mb-1 vp-muted">Type</label>
            <select
              value={f.field_type}
              onChange={(e) => onUpdate({ field_type: e.target.value })}
              data-testid={`cb-field-type-${idx}`}
              disabled={isMaster}
              className={`vp-select !py-2 !text-sm w-full ${isMaster ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div className="sm:col-span-3 flex items-end gap-2">
            <label className={`flex-1 inline-flex items-center gap-2 text-sm py-2 select-none ${isMaster ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}>
              <input
                type="checkbox"
                checked={!!f.required}
                onChange={(e) => onUpdate({ required: e.target.checked })}
                data-testid={`cb-field-required-${idx}`}
                disabled={isMaster}
                className="rounded accent-[hsl(var(--gold))]"
              />
              <span className="vp-muted">Required</span>
            </label>
            {isMaster ? (
              // Master fields cannot be deleted. Render nothing where the
              // delete button would sit — the "Master" badge on the label
              // is the visual cue that the row is locked.
              null
            ) : confirmDel ? (
              <div
                className="flex items-center gap-1 bg-[hsl(var(--bg-elevated))] border border-[hsl(var(--border))] rounded-md p-1 shadow-sm"
                data-testid={`cb-field-delete-confirm-${idx}`}
              >
                <button
                  type="button"
                  onClick={() => { setConfirmDel(false); onDelete(); }}
                  data-testid={`cb-field-delete-yes-${idx}`}
                  aria-label="Confirm delete field"
                  className="vp-btn vp-btn-sm !h-8 !px-2 bg-[hsl(var(--loss))] text-white hover:opacity-90"
                >
                  <Check size={13} />
                  <span className="text-xs font-medium">Delete</span>
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDel(false)}
                  data-testid={`cb-field-delete-cancel-${idx}`}
                  aria-label="Cancel delete"
                  className="vp-btn vp-btn-ghost vp-btn-sm !h-8 !w-8 !p-0 border border-[hsl(var(--border))]"
                >
                  <X size={13} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmDel(true)}
                data-testid={`cb-field-delete-${idx}`}
                aria-label="Delete field"
                className="vp-btn vp-btn-ghost vp-btn-sm !h-9 !w-9 !p-0 border border-[hsl(var(--loss))] text-[hsl(var(--loss))]"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
          {needsOptions && (
            <div className="sm:col-span-12">
              <label className="block text-[10px] font-medium uppercase tracking-wider mb-1 vp-muted">Options (one per line)</label>
              <textarea
                rows={3}
                value={optionsText}
                onChange={(e) => setOptionsText(e.target.value)}
                onBlur={() => {
                  const next = optionsText.split("\n").map((s) => s.trim()).filter(Boolean);
                  onUpdate({ options: next });
                }}
                data-testid={`cb-field-options-${idx}`}
                placeholder={"Mint\nExcellent\nGood\nFair\nPoor"}
                className="vp-textarea w-full !text-sm font-mono"
              />
            </div>
          )}
        </div>
      </div>
    </li>
  );
}


/* ============================================================
   Rare Values Scan — admin-only Legacy Duplicate Cleaner (P2).
   Scans the current category via GET /api/categories/{id}/rare-values
   and surfaces rare values (used by 1 item), duplicate groups (near-
   duplicate variants of the same value), and unused options. Actions
   are strictly Merge (rewrite from_value → to_value on item_data) and
   Delete (blank the value on items, or remove from options if no
   items use it). No schema / preset / field-name changes.
   ============================================================ */
function RareValuesScan({ catId }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [busyKey, setBusyKey] = useState("");
  const [open, setOpen] = useState(false);
  const [fixAllBusy, setFixAllBusy] = useState(false);

  const runScan = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await apiClient.get(`/categories/${catId}/rare-values`);
      setReport(data);
      setOpen(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Scan failed.");
    } finally {
      setLoading(false);
    }
  };

  const merge = async (fieldName, fromValue, toValue) => {
    if (!toValue || fromValue === toValue) return;
    const key = `merge::${fieldName}::${fromValue}`;
    setBusyKey(key);
    try {
      const { data } = await apiClient.post(`/categories/${catId}/rare-values/merge`, {
        field_name: fieldName,
        from_value: fromValue,
        to_value: toValue,
      });
      toast.success(`Merged "${fromValue}" → "${toValue}" (${data.updated_count} item${data.updated_count === 1 ? "" : "s"}).`);
      await runScan();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed.");
    } finally {
      setBusyKey("");
    }
  };

  // P4 — Fix All Near-Duplicates. Client-side normaliser that groups
  // case-only, whitespace-only, AND punctuation-only variants of the same
  // value (the backend's own normaliser only handles case + whitespace).
  //   • Lower-case
  //   • Common punctuation → space  ( - _ . / , ; : ( ) ' " ` )
  //   • Collapse repeated whitespace
  //   • Trim
  // Highest-count variant in each group wins; ties broken by first-seen
  // (stable server order). Uses the existing single-value merge endpoint
  // one call per non-canonical variant — no new backend surface.
  const normaliseFuzzy = (s) =>
    String(s ?? "")
      .toLowerCase()
      .replace(/[\-_./,;:()'"`]/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const fixAllNearDuplicates = async () => {
    if (!report || fixAllBusy) return;
    // Build the merge plan up front: [{fieldName, fromValue, toValue}, …]
    const plan = [];
    for (const f of report.fields || []) {
      const groups = new Map();
      for (const v of f.values || []) {
        const key = normaliseFuzzy(v.value);
        if (!key) continue;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push({ value: v.value, count: v.count });
      }
      for (const variants of groups.values()) {
        if (variants.length < 2) continue;
        const sorted = [...variants].sort((a, b) => b.count - a.count);
        const canonical = sorted[0].value;
        for (const v of sorted.slice(1)) {
          plan.push({ fieldName: f.field_name, fromValue: v.value, toValue: canonical });
        }
      }
    }
    if (plan.length === 0) {
      toast.info("No case / whitespace / punctuation variants to merge.");
      return;
    }
    setFixAllBusy(true);
    let ok = 0;
    let failed = 0;
    for (const p of plan) {
      try {
        await apiClient.post(`/categories/${catId}/rare-values/merge`, {
          field_name: p.fieldName,
          from_value: p.fromValue,
          to_value: p.toValue,
        });
        ok += 1;
      } catch {
        failed += 1;
      }
    }
    if (failed === 0) {
      toast.success(`Merged ${ok} near-duplicate variant${ok === 1 ? "" : "s"}.`);
    } else {
      toast.warning(`Merged ${ok} variant${ok === 1 ? "" : "s"} · ${failed} failed.`);
    }
    await runScan();
    setFixAllBusy(false);
  };

  // Detect if the current report contains any near-duplicate groups under
  // the fuzzy (case + whitespace + punctuation) normaliser — so we can
  // enable/disable the Fix All button precisely.
  const fuzzyGroupCount = (() => {
    if (!report) return 0;
    let n = 0;
    for (const f of report.fields || []) {
      const groups = new Map();
      for (const v of f.values || []) {
        const key = normaliseFuzzy(v.value);
        if (!key) continue;
        if (!groups.has(key)) groups.set(key, 0);
        groups.set(key, groups.get(key) + 1);
      }
      for (const c of groups.values()) if (c > 1) n += 1;
    }
    return n;
  })();

  const del = async (fieldName, value) => {
    const key = `delete::${fieldName}::${value}`;
    setBusyKey(key);
    try {
      const { data } = await apiClient.post(`/categories/${catId}/rare-values/delete`, {
        field_name: fieldName,
        value,
      });
      if (data.options_updated) {
        toast.success(`Removed unused option "${value}".`);
      } else {
        toast.success(`Cleared "${value}" from ${data.updated_count} item${data.updated_count === 1 ? "" : "s"}.`);
      }
      await runScan();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    } finally {
      setBusyKey("");
    }
  };

  const findingsPerField = (f) =>
    (f.values || []).filter((v) => v.rare).length +
    (f.duplicate_groups || []).length +
    (f.unused_options || []).length;

  const fieldsWithFindings = (report?.fields || []).filter((f) => findingsPerField(f) > 0);

  return (
    <section className="vp-card p-6 mb-6" data-testid="cb-rare-values-section">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold">Rare Values Scan</h2>
          <p className="text-xs vp-muted mt-1">
            Admin-only. Surfaces mis-typed variants, rare values (used by 1 item),
            and unused dropdown options in this category. Merge / Delete never
            touches the field schema.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={fixAllNearDuplicates}
            disabled={!report || fixAllBusy || loading || fuzzyGroupCount === 0}
            data-testid="cb-rare-values-fix-all-btn"
            title={
              !report
                ? "Run a scan first"
                : fuzzyGroupCount === 0
                ? "No case / whitespace / punctuation variants detected"
                : `Merge ${fuzzyGroupCount} near-duplicate group${fuzzyGroupCount === 1 ? "" : "s"} into the highest-count spelling`
            }
            className="vp-btn vp-btn-ghost vp-btn-sm border border-[hsl(var(--border))] disabled:opacity-40"
          >
            {fixAllBusy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            Fix All Near-Duplicates
          </button>
          <button
            type="button"
            onClick={runScan}
            disabled={loading || fixAllBusy}
            data-testid="cb-rare-values-scan-btn"
            className="vp-btn vp-btn-secondary vp-btn-sm disabled:opacity-50"
          >
            {loading ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            {report ? "Re-scan" : "Run Scan"}
          </button>
        </div>
      </div>

      {error ? (
        <p className="text-xs mt-3 text-[hsl(var(--loss))]" data-testid="cb-rare-values-error">{error}</p>
      ) : null}

      {open && report ? (
        <div className="mt-4 space-y-4" data-testid="cb-rare-values-report">
          <p className="text-xs vp-muted">
            Scanned {report.items_scanned} item{report.items_scanned === 1 ? "" : "s"}.{" "}
            {fieldsWithFindings.length === 0
              ? "No rare values, duplicates, or unused options found."
              : `${fieldsWithFindings.length} field${fieldsWithFindings.length === 1 ? "" : "s"} with findings.`}
          </p>

          {fieldsWithFindings.map((f) => {
            const otherValues = (f.values || []).map((v) => v.value);
            return (
              <div
                key={f.field_name}
                className="border border-[hsl(var(--border))] rounded-md p-3"
                data-testid={`cb-rare-values-field-${f.field_name}`}
              >
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="text-sm font-semibold">{f.field_name}</span>
                  <span className="text-[10px] uppercase tracking-wider vp-muted">{f.field_type}</span>
                  {f.master ? (
                    <span className="text-[10px] uppercase tracking-wider border border-[hsl(var(--gold))] text-[hsl(var(--gold))] rounded-full px-1.5">Master</span>
                  ) : null}
                </div>

                {(f.duplicate_groups || []).length > 0 ? (
                  <div className="mb-3">
                    <p className="text-[11px] font-medium uppercase tracking-wider vp-muted mb-1">Near-duplicate variants</p>
                    <ul className="space-y-2">
                      {f.duplicate_groups.map((g) => {
                        const top = g.variants[0];
                        return (
                          <li key={g.normalised} className="flex items-center gap-2 flex-wrap text-sm">
                            <span className="vp-muted text-xs">Group:</span>
                            {g.variants.map((v) => (
                              <span
                                key={v.value}
                                className="border border-[hsl(var(--border))] rounded-md px-2 py-0.5 text-xs"
                                data-testid={`cb-rare-values-variant-${f.field_name}-${v.value}`}
                              >
                                {v.value} <span className="vp-muted">×{v.count}</span>
                              </span>
                            ))}
                            <span className="vp-muted text-xs">→ merge all into</span>
                            <select
                              defaultValue={top.value}
                              onChange={(e) => {
                                const target = e.target.value;
                                for (const v of g.variants) {
                                  if (v.value !== target) merge(f.field_name, v.value, target);
                                }
                              }}
                              data-testid={`cb-rare-values-group-merge-${f.field_name}-${g.normalised}`}
                              disabled={!!busyKey}
                              className="vp-select !h-7 !text-xs"
                            >
                              {g.variants.map((v) => (
                                <option key={v.value} value={v.value}>{v.value}</option>
                              ))}
                            </select>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : null}

                {(f.values || []).some((v) => v.rare) ? (
                  <div className="mb-3">
                    <p className="text-[11px] font-medium uppercase tracking-wider vp-muted mb-1">Rare values (used by 1 item)</p>
                    <ul className="space-y-1.5">
                      {f.values.filter((v) => v.rare).map((v) => (
                        <RareValueRow
                          key={v.value}
                          fieldName={f.field_name}
                          value={v.value}
                          otherValues={otherValues.filter((x) => x !== v.value)}
                          busyKey={busyKey}
                          onMerge={merge}
                          onDelete={del}
                        />
                      ))}
                    </ul>
                  </div>
                ) : null}

                {(f.unused_options || []).length > 0 ? (
                  <div>
                    <p className="text-[11px] font-medium uppercase tracking-wider vp-muted mb-1">Unused options (in schema, 0 items)</p>
                    <ul className="flex flex-wrap gap-2">
                      {f.unused_options.map((opt) => (
                        <li key={opt} className="flex items-center gap-1">
                          <span className="border border-[hsl(var(--border))] rounded-md px-2 py-0.5 text-xs">{opt}</span>
                          <button
                            type="button"
                            onClick={() => del(f.field_name, opt)}
                            disabled={busyKey === `delete::${f.field_name}::${opt}`}
                            data-testid={`cb-rare-values-orphan-delete-${f.field_name}-${opt}`}
                            className="vp-btn vp-btn-ghost vp-btn-sm !h-6 !px-2 border border-[hsl(var(--loss))] text-[hsl(var(--loss))]"
                          >
                            Delete
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}

function RareValueRow({ fieldName, value, otherValues, busyKey, onMerge, onDelete }) {
  const [target, setTarget] = useState("");
  const mergeKey = `merge::${fieldName}::${value}`;
  const delKey = `delete::${fieldName}::${value}`;
  return (
    <li className="flex items-center gap-2 flex-wrap text-sm" data-testid={`cb-rare-values-row-${fieldName}-${value}`}>
      <span className="border border-[hsl(var(--border))] rounded-md px-2 py-0.5 text-xs">{value}</span>
      <span className="vp-muted text-xs">merge into</span>
      <select
        value={target}
        onChange={(e) => setTarget(e.target.value)}
        data-testid={`cb-rare-values-merge-target-${fieldName}-${value}`}
        disabled={!!busyKey || otherValues.length === 0}
        className="vp-select !h-7 !text-xs"
      >
        <option value="">— pick target —</option>
        {otherValues.map((v) => (
          <option key={v} value={v}>{v}</option>
        ))}
      </select>
      <button
        type="button"
        onClick={() => onMerge(fieldName, value, target)}
        disabled={!target || busyKey === mergeKey}
        data-testid={`cb-rare-values-merge-${fieldName}-${value}`}
        className="vp-btn vp-btn-secondary vp-btn-sm !h-7 !px-2 disabled:opacity-40"
      >
        {busyKey === mergeKey ? <Loader2 size={11} className="animate-spin" /> : null}
        Merge
      </button>
      <button
        type="button"
        onClick={() => onDelete(fieldName, value)}
        disabled={busyKey === delKey}
        data-testid={`cb-rare-values-delete-${fieldName}-${value}`}
        className="vp-btn vp-btn-ghost vp-btn-sm !h-7 !px-2 border border-[hsl(var(--loss))] text-[hsl(var(--loss))] disabled:opacity-40"
      >
        {busyKey === delKey ? <Loader2 size={11} className="animate-spin" /> : null}
        Delete
      </button>
    </li>
  );
}
